<template>
  <TextBubble
    :id="`root-${rootTextCell.annotation._id}`"
    class="rootText-cell"
    :emphasize="
      annotationHoverIds && Object.keys(annotationHoverIds).includes(rootTextCell.annotation._id)
    "
    :style="{
      position: 'relative',
      'z-index': hover ? 9 : undefined,
    }"
    :hideBoxShadow="hideBoxShadow"
    :disabled="true"
    color="#333"
    :shadow="true"
    @click.prevent="handleSingleAndDoubleClick($event, doDblClick)"
    @hoverIn="hoverIn"
    @hoverOut="hoverOut"
  >
    <template #default>
      <div class="uk-flex uk-flex-between">
        <div class="root-text uk-width-expand" v-html="rootText"></div>
        <div
          v-if="counterpointData.rounds.length > 4 && !verticalView"
          @click="showVerticalView"
          uk-tooltip="Threads view"
          :style="{ left: 0, top: 0, 'z-index': 1, cursor: 'pointer' }"
          class="uk-width-auto thred-view-button"
        >
          <img src="/icons/icon-expand-thread.svg" />
        </div>
      </div>
      <div class="arrows">
        <template v-if="counterpointLoaded && !verticalView">
          <AnnotationArrow
            v-for="ann in annotationData"
            :key="ann._id"
            :annotation="ann"
            arrow-type="block"
            :ref="`root-text-arrow-${annotation._id}`"
            type="root"
          />
        </template>
      </div>
    </template>

    <template #metadata>
      <span>{{ author }}</span>
    </template>
  </TextBubble>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import TextBubble from '@/components/display/general/TextBubble';
import AnnotationArrow from '@/components/annotations/AnnotationArrow';
import { getElementAbsolutePosition, dragElement } from '@/assets/js/utils';
import { fixedRowHeight, rootTextMaxChars } from '@/assets/js/constants';
import { singleDoubleClicksMixin } from '@/assets/js/mixins/singleDoubleClicksMixin';
import UIkit from 'uikit';

export default {
  name: 'RootTextCell',

  mixins: [singleDoubleClicksMixin],

  components: {
    TextBubble,
    AnnotationArrow,
  },

  props: {
    rootTextCell: {
      type: Object,
      required: true,
    },
    author: String,
    hideBoxShadow: Boolean,
    verticalView: Boolean,
  },

  data() {
    return {
      mounted: false,
      hover: false,
      activeAnnotations: [],
      countMouseOver: 0,
    };
  },

  computed: {
    UIkit() {
      return UIkit;
    },
    fixedRowHeight() {
      return fixedRowHeight;
    },

    ...mapState([
      'annotationHoverIds',
      'counterpointGeometries',
      'counterpointLoaded',
      'scrollCounter',
      'counterpointData',
    ]),

    ...mapGetters(['arrowsArray', 'getRefAnnotationsIds', 'myLatestRoundNumber']),

    rootText() {
      return !this.hover && this.rootTextCell.rootText.length > rootTextMaxChars
        ? this.rootTextCell.rootText.substring(0, rootTextMaxChars).trim() + '...'
        : this.rootTextCell.rootText;
    },

    /**
     * Annotation linked to this root-text cell
     */
    annotation() {
      return this.rootTextCell.annotation;
    },

    annotationData() {
      if (this.counterpointData.rounds.length > 4) {
        let ids = this.getRefAnnotationsIds(this.rootTextCell.annotation._id);
        let ann = [
          ...this.counterpointData.rounds[
            this.counterpointData.rounds.length - 4
          ]?.annotations?.filter(item => ids.includes(item._id)),
        ];
        ann = ann.map(item => {
          return {
            ...item,
            parentAnn: this.rootTextCell.annotation._id,
          };
        });
        return [...ann];
      }
      return [this.rootTextCell.annotation];
    },

    rootTextCellGeometry() {
      const rootTextCell = this.$refs.rootTextCell;
      if (!this.mounted || !rootTextCell)
        return {
          top: 0,
          height: 0,
          left: 0,
          width: 0,
        };

      let rootTextCellGeometry = getElementAbsolutePosition(rootTextCell.$el);
      rootTextCellGeometry.height = rootTextCell.$el.offsetHeight;
      rootTextCellGeometry.width = rootTextCell.$el.offsetWidth;

      return rootTextCellGeometry;
    },
  },

  mounted() {
    this.mounted = true;
  },

  methods: {
    ...mapMutations(['setVerticalView', 'setVerticalViewId']),

    hoverIn(event) {
      this.hover = true;
      this.$emit('hoverIn', this.rootTextCell, event);
      this.countMouseOver = setTimeout(() => {
        this.$emit('click', event);
      }, 700);
    },

    hoverOut() {
      this.hover = false;
      clearTimeout(this.countMouseOver);
      this.$emit('hoverOut');
    },

    doDblClick(e) {
      if (
        e.target.classList.value !== 'uk-width-auto thred-view-button' &&
        e.target.classList.value !== ''
      ) {
        this.$emit('dblclick', `counterpoint-ref-${this.rootTextCell.annotation._id}`);
      }
    },

    async showVerticalView() {
      this.activeAnnotations = [];
      this.getLatestAnnotationId(this.rootTextCell.annotation._id);
      this.setVerticalView(true);
      setTimeout(() => {
        UIkit.modal(document.getElementById('verticalViewModal')).show();
      }, 200);
      setTimeout(() => {
        dragElement(document.getElementsByClassName('vertical-modal')[0], 'modalHeader');
      }, 500);
    },

    getLatestAnnotationId(id) {
      let annArray = [];
      this.counterpointData.counterpointsFlat.map(data => {
        if (data.refAnnotation && data.refAnnotation._id === id) {
          annArray.push(data._id);
        }
      });
      if (annArray.length) {
        this.getLatestAnnotationId(annArray[0]);
      } else {
        let latestIndex = 0;
        this.counterpointData.rounds.map((data, index) => {
          let latestRound = data.annotations.filter(ann => ann._id === id);
          if (latestRound.length) {
            latestIndex = index;
          }
        });
        this.activeAnnotations = new Array(this.myLatestRoundNumber - 1);
        this.setActiveAnnotations(id, latestIndex);
      }
    },

    setActiveAnnotations(id, index) {
      this.counterpointData.counterpointsFlat.map(data => {
        if (data._id === id) {
          if (data.refAnnotation && data.refAnnotation._id) {
            this.activeAnnotations[index] = id;
            this.setActiveAnnotations(data.refAnnotation._id, index - 1);
          } else {
            this.activeAnnotations[index] = id;
            this.setVerticalViewId(this.activeAnnotations);
          }
        }
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.rootText
  &-cell
    width: 100%
    margin-top: 1rem
    margin-bottom: 100px
    position: absolute
  &-wrapper
    border: .5px solid transparent
</style>
